import React, { useState, useEffect } from "react";
import api, { usersApi } from "../../services/api";
import { useAuth } from "../../hooks/useAuth";
import { Input } from "../../components/ui/Input";

interface User {
  id: number;
  email: string;
  permissions?: string[];
  created_at: string;
  updated_at: string;
}

interface PermissionItem {
  key: string;
  description: string;
}

const UsersPage: React.FC = () => {
  const [users, setUsers] = React.useState<User[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [permissionsCsv, setPermissionsCsv] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [allPermissions, setAllPermissions] = useState<PermissionItem[]>([]);
  const [permModalUser, setPermModalUser] = useState<User | null>(null);
  const [permSelections, setPermSelections] = useState<Record<string, boolean>>(
    {},
  );
  const [savingPerms, setSavingPerms] = useState(false);
  const { user: authUser } = useAuth();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [resUsers, resPerms] = await Promise.all([
        api.get("/users"),
        usersApi.listPermissions(),
      ]);
      setUsers(resUsers.data);
      setAllPermissions(resPerms || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const permissions = permissionsCsv
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      await api.post("/users", { email, password, permissions });
      setEmail("");
      setPassword("");
      setPermissionsCsv("");
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Failed to create user");
    }
  };

  const openPermsModal = (user: User) => {
    setPermModalUser(user);
    const current = new Set(user.permissions || []);
    const initial: Record<string, boolean> = {};
    (allPermissions || []).forEach((p) => {
      initial[p.key] = current.has(p.key);
    });
    setPermSelections(initial);
  };

  const togglePerm = (key: string) => {
    setPermSelections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const savePerms = async () => {
    if (!permModalUser) return;
    setSavingPerms(true);
    try {
      const next = Object.keys(permSelections).filter((k) => permSelections[k]);
      await usersApi.updateUser(permModalUser.id, { permissions: next });
      setPermModalUser(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Failed to update permissions");
    } finally {
      setSavingPerms(false);
    }
  };

  const onDelete = async (u: User) => {
    if (authUser && authUser.id === u.id) {
      setError("You cannot delete your own user account.");
      return;
    }
    if (!confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    setError(null);
    try {
      await usersApi.deleteUser(u.id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Failed to delete user");
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Users</h1>
      <form
        onSubmit={onCreate}
        className="flex flex-wrap gap-2 mb-4 items-center"
      >
        <Input
          type="text"
          placeholder="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          variant="small"
          showLabel={false}
          className="border px-2 py-1"
        />
        <Input
          type="password"
          placeholder="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          variant="small"
          showLabel={false}
          className="border px-2 py-1"
        />
        <Input
          type="text"
          placeholder="permissions (comma-separated) e.g. flows.create, flows.edit, flows.review"
          value={permissionsCsv}
          onChange={(e) => setPermissionsCsv(e.target.value)}
          variant="small"
          showLabel={false}
          className="border px-2 py-1 min-w-[260px]"
        />
        <button
          className="bg-indigo-600 text-white px-3 py-1 rounded"
          type="submit"
        >
          Create
        </button>
      </form>
      {error && <div className="text-red-600 text-sm mb-2">{error}</div>}
      {loading ? (
        <div>Loading...</div>
      ) : (
        <table className="min-w-full bg-white">
          <thead>
            <tr>
              <th className="text-left p-2">ID</th>
              <th className="text-left p-2">Email</th>
              <th className="text-left p-2">Permissions</th>
              <th className="text-left p-2">Created</th>
              <th className="text-left p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t">
                <td className="p-2">{u.id}</td>
                <td className="p-2">{u.email}</td>
                <td className="p-2 text-xs text-gray-700">
                  {(u.permissions || []).join(", ")}
                </td>
                <td className="p-2">
                  {new Date(u.created_at).toLocaleString()}
                </td>
                <td className="p-2">
                  <button
                    className="px-2 py-1 text-sm bg-gray-100 border rounded hover:bg-gray-200"
                    onClick={() => openPermsModal(u)}
                  >
                    Manage Permissions
                  </button>
                  <button
                    className="ml-2 px-2 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                    onClick={() => onDelete(u)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {permModalUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded shadow-lg w-full max-w-2xl">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                Manage Permissions — {permModalUser.email}
              </h2>
              <button
                className="text-sm text-gray-600"
                onClick={() => setPermModalUser(null)}
              >
                Close
              </button>
            </div>
            <div className="p-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {allPermissions.map((p) => (
                  <label
                    key={p.key}
                    className="flex items-start gap-2 border rounded p-2"
                  >
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={!!permSelections[p.key]}
                      onChange={() => togglePerm(p.key)}
                    />
                    <div>
                      <div className="font-mono text-sm">{p.key}</div>
                      <div className="text-xs text-gray-600">
                        {p.description}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div className="p-4 border-t flex items-center justify-end gap-2">
              <button
                className="px-4 py-2 border rounded"
                onClick={() => setPermModalUser(null)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-indigo-600 text-white rounded disabled:opacity-50"
                disabled={savingPerms}
                onClick={savePerms}
              >
                {savingPerms ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
